/**
 * @file DeliveryControlCard.tsx
 * @description Пороги контроля отправлений (phase19.13 шаг 5).
 *
 * Три числа, по которым отправление попадает в список «Требуют внимания».
 * Поля описаны реестром `THRESHOLD_FIELDS`: новый порог = запись, а не ветка
 * в разметке. Валидируем только «целое ≥ 1» — остальное скажет сервер.
 *
 * Сохранение кладёт свежие значения в кэш `useDeliverySettings`, чтобы бейджи
 * «зависла» на карточках отгрузок пересчитались без перезагрузки страницы.
 */

import { Loader2, Save } from "lucide-react";
import { useEffect, useState } from "react";

import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { EditorialCard } from "../ui/EditorialCard";
import { useNotify } from "../ui/NotifyProvider";
import { useHasPermission } from "../../auth/hooks";
import { primeDeliverySettings } from "../../hooks/useDeliverySettings";
import { ApiError } from "../../lib/api-client";
import {
  getDeliverySettings,
  updateDeliverySettings,
  type DeliverySettingsDto,
} from "../../lib/api-delivery-monitoring";

/** Поля порогов: ключ DTO, подпись, единица и объяснение правила. */
const THRESHOLD_FIELDS: Array<{
  key: keyof DeliverySettingsDto;
  label: string;
  hint: string;
}> = [
  {
    key: "staleTrackTimeoutHours",
    label: "Трек не присвоен, часов",
    hint: "Заявка ушла перевозчику, а номер накладной так и не пришёл.",
  },
  {
    key: "staleNoMovementDays",
    label: "Без движения, дней",
    hint: "Статус отправления не менялся дольше этого срока.",
  },
  {
    key: "stalePickupWaitDays",
    label: "Лежит в ПВЗ, дней",
    hint: "Клиент не забирает посылку — дальше перевозчик вернёт её отправителю.",
  },
];

/** Строковая форма: пустое поле не должно превращаться в 0. */
type FormState = Record<keyof DeliverySettingsDto, string>;

function toForm(dto: DeliverySettingsDto): FormState {
  return {
    staleTrackTimeoutHours: String(dto.staleTrackTimeoutHours),
    staleNoMovementDays: String(dto.staleNoMovementDays),
    stalePickupWaitDays: String(dto.stalePickupWaitDays),
  };
}

export function DeliveryControlCard() {
  const notify = useNotify();
  const canManage = useHasPermission("SHOP_MANAGE_CARRIERS");
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    getDeliverySettings()
      .then((dto) => {
        if (alive) setForm(toForm(dto));
      })
      .catch((err: unknown) => {
        if (alive) {
          setError(err instanceof ApiError ? err.message : "Не удалось загрузить пороги");
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  async function onSave(): Promise<void> {
    if (!form) return;
    const patch: Partial<DeliverySettingsDto> = {};
    for (const field of THRESHOLD_FIELDS) {
      const value = Number(form[field.key]);
      if (!Number.isInteger(value) || value < 1) {
        notify.error(`«${field.label}» — целое число не меньше 1`);
        return;
      }
      patch[field.key] = value;
    }
    setSaving(true);
    try {
      const saved = await updateDeliverySettings(patch);
      setForm(toForm(saved));
      primeDeliverySettings(saved);
      notify.success("Пороги контроля сохранены");
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  }

  return (
    <EditorialCard>
      <EditorialCard.Header
        title="Контроль отправлений"
        subtitle="Когда отправление попадает в список «Требуют внимания»"
      />
      <EditorialCard.Body className="flex flex-col gap-4">
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="size-5 animate-spin text-on-surface-variant" />
          </div>
        ) : !form ? (
          <p className="text-sm text-on-surface-variant">
            {error ?? "Пороги недоступны."}
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {THRESHOLD_FIELDS.map((field) => (
                <div key={field.key} className="flex flex-col gap-1.5">
                  <Label htmlFor={field.key}>{field.label}</Label>
                  <Input
                    id={field.key}
                    inputMode="numeric"
                    value={form[field.key]}
                    disabled={!canManage}
                    onChange={(e) =>
                      setForm((prev) =>
                        prev ? { ...prev, [field.key]: e.target.value } : prev,
                      )
                    }
                  />
                  <p className="text-xs text-on-surface-variant">{field.hint}</p>
                </div>
              ))}
            </div>
            {canManage ? (
              <div className="flex justify-end">
                <Button className="min-h-11" onClick={() => void onSave()} disabled={saving}>
                  {saving ? (
                    <Loader2 className="mr-1.5 size-4 animate-spin" />
                  ) : (
                    <Save className="mr-1.5 size-4" />
                  )}
                  Сохранить пороги
                </Button>
              </div>
            ) : null}
          </>
        )}
      </EditorialCard.Body>
    </EditorialCard>
  );
}
